// src/pages/SaleInvoice.jsx
import { useEffect, useState, useCallback } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Paper, Stack, TextField, Typography, useMediaQuery, useTheme} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { saveAs } from "file-saver";

const API_BASE = "http://192.168.1.248:3103";

const jsonToCsv = (rows) => {
    if (!rows.length) return "";
    const headers = Object.keys(rows[0]);
    const escape = (s) => `"${(s ?? "").toString().replace(/"/g, '""')}"`;
    const lines = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))];
    return lines.join("\n");
};

export default function SaleInvoice3() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [filters, setFilters] = useState({
        clientName: "", billNo: "", state: "", city: "", installBy: ""
    });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openReturnDialog, setOpenReturnDialog] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

    const fetchData = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const { data: resp } = await axios.get(`${API_BASE}/api/get-invoice`, { params });
            setData(resp || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleView = (id) => {
        window.open(`${window.location.origin}/invoice/${id}`, `_blank`);
    };

    const handleExport = () => {
        const csv = jsonToCsv(data);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "invoices.csv");
    };

    const handleReturn = (id) => {
        setSelectedInvoiceId(id);
        setOpenReturnDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenReturnDialog(false);
        setSelectedInvoiceId(null);
    };

    const handleReturnAndCancel = () => {
        sendReturnRequest("cancel");
        handleCloseDialog();
    };

    const handleKeyModifiedReturn = () => {
        sendReturnRequest("return_payment");
        handleCloseDialog();
    };

    const sendReturnRequest = async (actionType) => {
        try {
            await axios.post(`${API_BASE}/api/return-invoice`, {
                invoiceId: selectedInvoiceId,
                actionType,
            });
        } catch (err) {
            console.error("API Error:", err);
        } finally {
            handleCloseDialog();
            fetchData();
        }
    };

    const columns = [
        { field: 'saleInvoiceNumber', headerName: 'Bill No', flex: 1 },
        { field: 'referenceNumber', headerName: 'Reference', flex: 1 },
        { field: 'companyName', headerName: 'Client Name', flex: 1.5 },
        { field: 'city', headerName: 'City', flex: 1 },
        { field: 'state', headerName: 'State', flex: 1 },
        { field: 'installBy', headerName: 'Installed By', flex: 1 },
        {
            field: 'statusName', headerName: 'Status', flex: 1,
            renderCell: (params) => (
                <Typography fontWeight={600} color="primary.main">{params.value}</Typography>
            )
        },
        {
            field: 'actions', headerName: 'Actions', flex: 1.2, sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" size="small" onClick={() => handleView(params.row.saleInvoiceId)}>View</Button>
                    {/* <Button variant="contained" size="small" color="secondary" onClick={() => handleReturn(params.row.saleInvoiceId)}>Return</Button> */}
                </Stack>
            )
        }
    ];

    return (
        <Box sx={{ p: isMobile ? 2 : 4 }}>
            <Typography variant="h4" gutterBottom align="center">Sale Invoices</Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button variant="outlined" onClick={handleExport}>Export CSV</Button>
            </Box>

            <Box>
                <DataGrid
                    rows={data}
                    columns={columns}
                    getRowId={(row) => row.saleInvoiceId}
                    autoHeight
                    loading={loading}
                    pageSize={10}
                    rowsPerPageOptions={[10, 20, 50]}
                    disableSelectionOnClick
                    sx={{
                        borderRadius: 2,
                        bgcolor: '#fff',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#cbd5e1',
                            fontWeight: 600,
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            backgroundColor: '#ffffff',
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            backgroundColor: '#f8fafc',
                        },
                    }}
                />
            </Box>

            {/* Return Dialog */}
            <Dialog open={openReturnDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        p: 2,
                        boxShadow: 8,
                        backgroundColor: theme.palette.background.paper,
                        transition: "all 0.3s ease-in-out",
                    },
                }}>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0 }}>
                    <Typography fontWeight={600}>Return Options</Typography>
                    <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 3 }}>
                    <Stack direction={isMobile ? "column" : "row"} spacing={2} justifyContent="center">
                        <Button variant="outlined" color="error" onClick={handleReturnAndCancel} fullWidth>Return & Cancel</Button>
                        <Button variant="contained" color="primary" onClick={handleKeyModifiedReturn} fullWidth>Key Modified & Return Payment</Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    );
}